import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState, LoadingState } from "../../components/states";
import { useToast } from "../../components/toast/toastContext";
import { fetchMcpTokenLifetimes, updateMcpTokenLifetimes, type McpTokenLifetimes } from "../../api/mcpTokenLifetimes";
import { NumberField, SettingForm, SettingSubsection } from "../../components/admin/SettingPanel";

const SettingsWritePermission = "settings.write";

interface Draft {
  accessTokenMinutes: string;
  refreshIdleDays: string;
  refreshAbsoluteDays: string;
}

function toDraft(lifetimes: McpTokenLifetimes): Draft {
  return {
    accessTokenMinutes: String(lifetimes.accessTokenMinutes),
    refreshIdleDays: String(lifetimes.refreshIdleDays),
    refreshAbsoluteDays: String(lifetimes.refreshAbsoluteDays),
  };
}

/**
 * How long a connected AI application's access lasts: the access-token lifetime in minutes, and the
 * refresh-token idle and absolute lifetimes in days. Saving applies to tokens issued afterwards; the server
 * refuses a value that is not positive, an access lifetime longer than the idle lifetime, or an idle
 * lifetime longer than the absolute one, and says why.
 */
export function McpTokenLifetimesSection(): React.JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded" } | { kind: "error"; message: string }>({ kind: "loading" });
  const [draft, setDraft] = useState<Draft>({ accessTokenMinutes: "", refreshIdleDays: "", refreshAbsoluteDays: "" });
  const [pending, setPending] = useState(false);
  const { show } = useToast();

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchMcpTokenLifetimes();
    if (result.kind === "ok") {
      setDraft(toDraft(result.response));
      setState({ kind: "loaded" });
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("admin.mcpTokenLifetimes.denied") });
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setPending(true);
    const result = await updateMcpTokenLifetimes(Number(draft.accessTokenMinutes), Number(draft.refreshIdleDays), Number(draft.refreshAbsoluteDays));
    setPending(false);
    if (result.kind === "ok") {
      setDraft(toDraft(result.response));
      show(t("admin.mcpTokenLifetimes.saved"), "success");
    } else if (result.kind === "denied") {
      show(t("admin.denied.description", { permissions: SettingsWritePermission }), "error");
    } else {
      show(result.message, "error");
    }
  };

  if (state.kind === "loading") {
    return <LoadingState label={t("admin.mcpTokenLifetimes.loading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState description={state.message} onRetry={() => void load()} />;
  }

  return (
    <SettingSubsection id="mcp-token-lifetimes" title={t("admin.mcpTokenLifetimes.title")} explanation={t("admin.mcpTokenLifetimes.explanation")}>
      <SettingForm>
        <NumberField id="mcp-access-token-minutes" label={t("admin.mcpTokenLifetimes.accessTokenMinutes")} min={1} value={draft.accessTokenMinutes} onChange={(accessTokenMinutes) => setDraft({ ...draft, accessTokenMinutes })} />
        <NumberField id="mcp-refresh-idle-days" label={t("admin.mcpTokenLifetimes.refreshIdleDays")} min={1} value={draft.refreshIdleDays} onChange={(refreshIdleDays) => setDraft({ ...draft, refreshIdleDays })} />
        <NumberField id="mcp-refresh-absolute-days" label={t("admin.mcpTokenLifetimes.refreshAbsoluteDays")} min={1} value={draft.refreshAbsoluteDays} onChange={(refreshAbsoluteDays) => setDraft({ ...draft, refreshAbsoluteDays })} />
        <button type="button" className="button button-secondary" disabled={pending} onClick={() => void save()}>
          {t("admin.mcpTokenLifetimes.save")}
        </button>
      </SettingForm>
    </SettingSubsection>
  );
}
